/**
 * The gap between "we sent you a code" and typing it in, kept across a page reload.
 *
 * This flow *requires* leaving the page: the code is in an email, so the applicant switches to
 * their mail app and comes back. On a mid-range Android phone that is exactly when the browser
 * tab gets evicted, and the state — which step, which address — lived only in React. Coming back
 * to an empty email box means requesting a second code, and the throttle allows three in fifteen
 * minutes, so a couple of reloads can lock someone out of their own application.
 *
 * `sessionStorage`, per tab, holding an address the person just typed on that device and nothing
 * else. It is deliberately not the answer mirror in `useFormState.ts`: answers want to survive a
 * flat battery and a closed tab, a half-finished sign-in does not.
 */

const storageKey = (scope: string): string => `sga:otp-step:${scope}`;

/** Matches CODE_TTL_MINUTES on the server. A code older than this can only be refused. */
const MAX_AGE_MS = 10 * 60_000;

export interface OtpStep {
  email: string;
  /** When the code was sent, so the resend cooldown resumes rather than restarting at zero. */
  sentAt: number;
}

export const readOtpStep = (scope: string): OtpStep | null => {
  try {
    const raw = window.sessionStorage.getItem(storageKey(scope));
    if (!raw) return null;

    const stored = JSON.parse(raw) as OtpStep;
    if (!stored?.email || Date.now() - stored.sentAt > MAX_AGE_MS) {
      window.sessionStorage.removeItem(storageKey(scope));
      return null;
    }
    return stored;
  } catch {
    return null;
  }
};

export const writeOtpStep = (scope: string, email: string): void => {
  try {
    window.sessionStorage.setItem(storageKey(scope), JSON.stringify({ email, sentAt: Date.now() }));
  } catch {
    /* Storage disabled. Signing in still works; a reload just costs another code. */
  }
};

export const clearOtpStep = (scope: string): void => {
  try {
    window.sessionStorage.removeItem(storageKey(scope));
  } catch {
    /* nothing useful to do */
  }
};

/** Seconds still to wait before the resend button is offered again, given when the code went out. */
export const resendSecondsLeft = (sentAt: number, cooldownSeconds = 60): number =>
  Math.max(0, Math.ceil((sentAt + cooldownSeconds * 1000 - Date.now()) / 1000));
